import axios from 'axios';
import type {
  MyPermissionsResponse,
  DemoEndpointResponse,
  SimulateResponse,
  RolePermissionMatrixResponse,
  DemoLevel,
} from '../types/permissionDemo';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: (err?.['code'] as string) ?? `HTTP-${response.status}`,
        message: (err?.['message'] as string) ?? 'Unknown error',
        detail: (err?.['detail'] as string) ?? undefined,
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const statusCode: number = error.response?.status ?? 0;
    const code = (errData?.['code'] as string) ?? `HTTP-${statusCode}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const detail = (errData?.['detail'] as string) ?? undefined;
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, detail, traceId, statusCode });
  }
);

export const permissionDemoApi = {
  /** GET /api/eraf/permission-demo/me/permissions */
  getMyPermissions: (): Promise<MyPermissionsResponse> =>
    apiClient
      .get<unknown, { data: MyPermissionsResponse }>('/permission-demo/me/permissions')
      .then((res) => res.data),

  /** GET /api/eraf/permission-demo/{level} */
  callDemoEndpoint: (level: DemoLevel): Promise<DemoEndpointResponse> =>
    apiClient
      .get<unknown, { data: DemoEndpointResponse }>(`/permission-demo/${level}`)
      .then((res) => res.data),

  /** GET /api/eraf/permission-demo/simulate?role=... (one role at a time via radio) */
  simulate: (role: string): Promise<SimulateResponse> =>
    apiClient
      .get<unknown, { data: SimulateResponse }>(
        `/permission-demo/simulate?role=${encodeURIComponent(role)}`
      )
      .then((res) => res.data),

  /** GET /api/eraf/permission-demo/matrix */
  getMatrix: (): Promise<RolePermissionMatrixResponse> =>
    apiClient
      .get<unknown, { data: RolePermissionMatrixResponse }>('/permission-demo/matrix')
      .then((res) => res.data),
};
